"use memo"

import {
  AutopilotTaskDto,
  useAssignTaskToUser,
  useInvalidateUseGetAutopilotTasksForCampaign,
} from '@urbanx/agx.activities.client'
import { AuthContext } from '@urbanx/agx.auth.client'
import {
  AgxButton,
  AgxEmptyState,
  AgxIconTask,
  AgxProgressBar,
  AgxSpacer,
  AgxTypography,
  IconTaskType,
  Icon_CheckmarkCircleOutline,
  Icon_Chevron,
  Icon_Clock,
} from '@urbanx/agx.ui-components2'
import Panel from 'components/panel/Panel'
import TaskAssigneeSelect from 'components/tasks/TaskAssigneeSelect'
import { ReactNode, useCallback, useContext, useEffect, useState } from 'react'
import { useAutopilotTaskUpdates } from 'hooks/useAutopilotTaskUpdates'
import { applyTaskUpdateToTasks, getStepCompletionPercentage } from 'utils/tasks'
import { splitPascalCase } from 'utils/stringUtils'
import { useGetAssigneeOptions } from '../TaskFilters/useGetAssigneeOptions'
import { TaskUpdatedNotification } from 'types/pushNotifications'

interface TaskModalEntryProps {
  title: string
  tasks: AutopilotTaskDto[]
  badge: ReactNode
  iconType: IconTaskType
  actionText: string
  onAction: (tasks: AutopilotTaskDto[]) => void
  notification?: ReactNode
  actionButtonId?: string
  panelKey?: string
}

const TaskModalEntry = ({
  title,
  tasks,
  badge,
  iconType,
  actionText,
  onAction,
  notification,
  actionButtonId = 'taskModalEntry-action',
  panelKey = 'TaskModalEntryPanel',
}: TaskModalEntryProps) => {
  const { userAccount } = useContext(AuthContext)
  const [displayTasks, setDisplayTasks] = useState(tasks)
  const [showCompletedSummary, setShowCompletedSummary] = useState(false)
  const allSteps = displayTasks.flatMap((task) => task.taskSteps ?? [])
  const campaignId = displayTasks[0]?.campaignId
  const contractId = displayTasks[0]?.contractId
  const allTasksCompleted = displayTasks.every(
    (task) => task.isCompleted === true
  )
  const allTasksAssigned = displayTasks.every((task) => !!task.assignedTo)
  const assignedTo = displayTasks[0]?.assignedTo
  const actionDisabled =
    !assignedTo || assignedTo !== userAccount.agent

  useEffect(() => {
    setDisplayTasks(tasks)
    setShowCompletedSummary(false)
  }, [tasks])

  const handleTaskUpdate = useCallback((taskUpdate: TaskUpdatedNotification) => {
    if (taskUpdate.task.isCompleted) {
      setShowCompletedSummary(false)
    }

    setDisplayTasks((prevTasks) =>
      applyTaskUpdateToTasks(prevTasks, taskUpdate)
    )
  }, [])

  useAutopilotTaskUpdates(handleTaskUpdate)

  const invalidateTasks = useInvalidateUseGetAutopilotTasksForCampaign()
  const { assigneeOptions } = useGetAssigneeOptions()
  const { assignTaskToUser } = useAssignTaskToUser({
    onSuccess: () => {
      if (campaignId) invalidateTasks(campaignId, contractId)
    },
    onError: () => setDisplayTasks(tasks),
  })

  return (
    <Panel key={panelKey} padding={'spacing-4xl'}>
      <AgxSpacer fill gap="spacing-2xl" style={{ width: '100%' }}>
        <AgxSpacer
          direction="horizontal"
          fill
          contentAlignment="center"
          contentJustification="space-between"
          style={{ width: '100%' }}
        >
          <AgxSpacer
            direction="horizontal"
            gap="spacing-lg"
            contentAlignment="center"
          >
            <AgxIconTask
              type={iconType}
              iconColor="black"
              height={32}
              width={32}
              bgColor="var(--bg-progress)"
            />
            <AgxTypography id={'title'} type={'heading'} size={'medium'}>
              {title}
            </AgxTypography>
          </AgxSpacer>
          <AgxSpacer
            direction="horizontal"
            contentJustification="end"
            contentAlignment="center"
            gap="2xs"
          >
            <TaskAssigneeSelect
              id={'assignedto'}
              value={displayTasks[0]?.assignedTo}
              options={assigneeOptions}
              onAssign={(userId) => {
                if (!campaignId) return

                setDisplayTasks((prevTasks) =>
                  prevTasks.map((task) => ({
                    ...task,
                    assignedTo: userId,
                  }))
                )

                displayTasks.forEach((task) => {
                  if (!task.category?.taskType) return

                  assignTaskToUser({
                    userId,
                    campaignId,
                    contractId: task.contractId,
                    category: task.category,
                  })
                })
              }}
            />
            {badge}
          </AgxSpacer>
        </AgxSpacer>
        {allTasksCompleted && (
          <AgxSpacer fill gap="spacing-2xl">
            <div
              role="button"
              tabIndex={0}
              onClick={() => setShowCompletedSummary((current) => !current)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  setShowCompletedSummary((current) => !current)
                }
              }}
              style={{ cursor: 'pointer', width: '100%' }}
            >
              <AgxSpacer
                direction="horizontal"
                gap="spacing-md"
                contentAlignment="center"
                fill
                contentJustification="start"
              >
                <Icon_CheckmarkCircleOutline color="var(--fg-success)" />
                <AgxTypography
                  id={`task-modal-entry-completed`}
                  type={'caption'}
                  size={'medium'}
                  color="var(--text-success)"
                >
                  Complete!
                </AgxTypography>
              </AgxSpacer>
            </div>
            {showCompletedSummary && (
              <div className="taskStep-checkboxContainer">
                <AgxSpacer fill gap="spacing-2xl">
                  {allSteps.map((step) => {
                    const name = splitPascalCase(step.name) ?? 'Untitled'
                    const key = `${step.order ?? 'no-order'}-${name}`

                    return (
                      <AgxSpacer
                        key={key}
                        direction="horizontal"
                        gap="spacing-md"
                        contentAlignment="end"
                        fill
                        contentJustification="start"
                      >
                        <Icon_CheckmarkCircleOutline color="var(--fg-success)" />
                        <AgxTypography
                          id="steps"
                          size="small"
                          type="body_ui"
                          color="var(--text-tertiary)"
                        >
                          {name}
                        </AgxTypography>
                      </AgxSpacer>
                    )
                  })}
                  {notification}
                </AgxSpacer>
              </div>
            )}
          </AgxSpacer>
        )}
        {!allTasksCompleted && (
          <>
            <AgxProgressBar
              variant="slim"
              id="progress"
              percentageProgressPosition="left"
              mode="dark"
              progress={getStepCompletionPercentage(allSteps)}
            />
            {allTasksAssigned ? (
              <AgxSpacer fill gap="spacing-3xl">
                <div className="taskStep-checkboxContainer">
                  <AgxSpacer fill gap="spacing-2xl">
                    {allSteps.map((step) => {
                      const name = splitPascalCase(step.name) ?? 'Untitled'
                      const key = `${step.order ?? 'no-order'}-${name}`

                      return (
                        <AgxSpacer
                          key={key}
                          direction="horizontal"
                          gap="spacing-md"
                          contentAlignment="end"
                          fill
                          contentJustification="start"
                        >
                          {step.isCompleted ? (
                            <Icon_CheckmarkCircleOutline color="var(--fg-success)" />
                          ) : (
                            <Icon_Clock color="var(--text-tertiary)" />
                          )}
                          <AgxTypography
                            id="steps"
                            size="small"
                            type="body_ui"
                            color="var(--text-tertiary)"
                          >
                            {name}
                          </AgxTypography>
                        </AgxSpacer>
                      )
                    })}
                    {notification}
                  </AgxSpacer>
                </div>
                <AgxSpacer
                  id="completeTaskRow"
                  direction="horizontal"
                  contentJustification="center"
                  fill
                  contentAlignment="center"
                >
                  <AgxButton
                    id={actionButtonId}
                    variant="primary"
                    size="small"
                    text={actionText}
                    rightIcon={
                      <Icon_Chevron
                        width={18}
                        height={18}
                        direction={'right'}
                        color="var(--fg-brand-main)"
                      />
                    }
                    disabled={actionDisabled}
                    onClick={() => onAction(displayTasks)}
                  />
                </AgxSpacer>
              </AgxSpacer>
            ) : (
              <AgxSpacer
                direction="horizontal"
                fill
                contentAlignment="center"
                contentJustification="center"
              >
                <AgxEmptyState
                  variant="advanced"
                  glyphType="locked"
                  actionButtons={[]}
                  label="Assign task to unlock"
                />
              </AgxSpacer>
            )}
          </>
        )}
      </AgxSpacer>
    </Panel>
  )
}

export default TaskModalEntry
